import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';

const AuthContext = createContext({});

// Track if we've already handled session expiry to prevent loops
let sessionExpiryHandled = false;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  // Show toast notification
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    
    // Auto-close toast after 5 seconds
    setTimeout(() => {
      setToast(null);
    }, 5000);
  };

  // Handle expired/corrupted session
  const handleExpiredSession = async () => {
    // Prevent multiple simultaneous calls
    if (sessionExpiryHandled) return;
    sessionExpiryHandled = true;

    // Only warn once in production
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Session expired or corrupted, clearing auth state');
    }

    // Clear auth state
    setUser(null);
    setUserProfile(null);

    // Show user-friendly toast
    showToast('Session expirée, veuillez vous reconnecter.', 'warning');

    // Clear session silently
    try {
      await supabase?.auth?.signOut();
    } catch (error) {
      // Silently handle signOut errors to avoid console noise
    }

    // Redirect to login after a short delay
    setTimeout(() => {
      navigate('/login');
      // Reset flag after navigation
      setTimeout(() => {
        sessionExpiryHandled = false;
      }, 1000);
    }, 500);
  };

  // Load user profile
  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error) throw error;
      
      // Convert snake_case to camelCase for JavaScript consistency
      const profileData = data ? {
        ...data,
        practitionerId: data?.practitioner_id,
        fullName: data?.full_name,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      } : null;
      
      setUserProfile(profileData);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error loading profile:', error);
      }
      setUserProfile(null);
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      // Handle refresh token errors
      if (error) {
        if (error?.message?.includes('refresh_token_not_found') || 
            error?.message?.includes('Invalid Refresh Token')) {
          handleExpiredSession();
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          console.error('Session retrieval error:', error);
        }
        setLoading(false);
        return;
      }

      // Check if this is a recovery session (password reset flow)
      const isRecoverySession = window.location?.pathname === '/password-reset' && 
                                window.location?.hash?.includes('type=recovery');
      
      // Don't set user state if we're in password reset flow
      if (!isRecoverySession) {
        setUser(session?.user ?? null);
        if (session?.user) {
          loadUserProfile(session?.user?.id);
        }
      }
      setLoading(false);
    })?.catch((error) => {
      // Handle session retrieval errors (e.g., invalid refresh token)
      if (error?.message?.includes('refresh_token_not_found') || 
          error?.message?.includes('Invalid Refresh Token')) {
        handleExpiredSession();
      } else if (process.env.NODE_ENV !== 'production') {
        console.error('Session retrieval error:', error);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      // Check if we're in password reset flow
      const isRecoverySession = window.location?.pathname === '/password-reset';
      
      // Don't process auth changes during password reset
      if (isRecoverySession && event !== 'SIGNED_OUT') {
        return;
      }
      
      // Handle token refresh errors
      if (event === 'TOKEN_REFRESHED' && !session) {
        handleExpiredSession();
        return;
      }

      // Handle signed out event
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        // Reset the flag when user explicitly signs out
        sessionExpiryHandled = false;
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
      }
    });

    return () => subscription?.unsubscribe();
  }, [navigate]);

  // Sign in
  const signIn = async (email, password) => {
    try {
      // Reset expiry flag on new sign in attempt
      sessionExpiryHandled = false;
      
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Sign in error:', error);
      }
      return { data: null, error };
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Reset expiry flag on explicit sign out
      sessionExpiryHandled = false;
      
      const { error } = await supabase?.auth?.signOut();
      
      // Handle "Auth session missing" error gracefully
      if (error && error?.message !== 'Auth session missing!') {
        // Only throw if it's NOT the session missing error
        throw error;
      }
      
      // Always clear local state regardless of signOut result
      setUser(null);
      setUserProfile(null);
      
      // Show logout toast
      showToast('Vous avez été déconnecté.', 'info');
      
      return { error: null };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Sign out error:', error);
      }
      
      // Even on error, clear local state and show toast
      setUser(null);
      setUserProfile(null);
      showToast('Vous avez été déconnecté.', 'info');
      
      return { error: null }; // Return success to allow redirect
    }
  };

  // Update email
  const updateEmail = async (newEmail) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        email: newEmail
      });
      
      if (error) throw error;
      
      // Update user_profiles table
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({ email: newEmail })
        ?.eq('id', user?.id);
      
      if (profileError) throw profileError;
      
      return { data, error: null };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Update email error:', error);
      }
      return { data: null, error };
    }
  };

  // Update password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Update password error:', error);
      }
      return { data: null, error };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signOut,
    updateEmail,
    updatePassword,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </AuthContext.Provider>
  );
};
